/**
 * A date and a time, with the time ALWAYS on a 24-hour clock.
 *
 * WHY THIS EXISTS RATHER THAN THE NATIVE datetime-local INPUT
 *
 * The native control renders its clock from the browser's locale, and
 * nothing in CSS or JavaScript can override it: an en-US browser draws
 * "08:15 PM" however the page is styled. That is wrong here for the reason
 * recorded against every other clock in this app -- a clinical record
 * cannot print a time that means two things, and AM/PM is the half that
 * gets dropped when a time is read aloud or copied onto a tube.
 *
 * It was ONCE BELIEVED that Chrome honours the element's `lang` here, and
 * this component shipped on that basis with its time half tagged en-GB.
 * IT DOES NOT. Verified against Chrome 147: `lang="en-GB"`, `lang="de"`,
 * an ancestor with `lang`, and a plain input all render the same 13:05 as
 * "01:05 PM". Chrome takes the format from the BROWSER's locale, and
 * nothing in the page reaches the shadow tree that draws it.
 *
 * So the halves are split for a different reason than first written: the
 * DATE keeps the native picker, which is genuinely wanted (mm/dd/yyyy under
 * en-US, and a real calendar on a tablet), and the TIME is <Time24 /> -- a
 * field of ours that speaks HH:mm and cannot be talked out of it.
 *
 * Value in, value out, is `YYYY-MM-DDTHH:mm` -- exactly what a
 * datetime-local emits, so this drops into the places that used one
 * without changing a single caller's parsing.
 *
 * ONE DIFFERENCE FROM A NATIVE INPUT, AND IT IS THE WHOLE CONTRACT:
 * `onChange` is handed the VALUE, not an event --
 *
 *     onChange={v => setWhen(v)}          correct
 *     onChange={e => setWhen(e.target.value)}   throws
 *
 * There is no event to give: the value is assembled from two controls, so
 * neither one's event describes it. Writing `e.target.value` reads
 * `.target` off a string and throws inside the handler, which in React
 * means the field silently refuses to accept anything typed into it.
 */
import React from 'react';
import Time24 from './Time24';

export default function DateTime24({
  value, onChange, disabled = false, style, dateStyle, timeStyle,
  required = false, autoFocus = false, id,
}) {
  const [datePart = '', timePart = ''] = String(value || '').split('T');

  const emit = (d, t) => {
    // A half-filled control emits nothing. Sending a date with no time
    // would silently mean midnight, and midnight is a real answer that
    // nobody typed.
    if (!d && !t) return onChange('');
    if (!d || !t) return onChange(`${d}T${t}`.replace(/T$/, ''));
    onChange(`${d}T${t.slice(0, 5)}`);
  };

  const base = {
    background: 'transparent', color: 'inherit',
    border: '1px solid var(--border-color, rgba(148,163,184,.4))',
    borderRadius: 6, padding: '3px 6px', fontSize: 12,
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', ...style }}>
      <input
        id={id}
        type="date"
        value={datePart}
        disabled={disabled}
        required={required}
        autoFocus={autoFocus}
        onChange={e => emit(e.target.value, timePart)}
        style={{ ...base, ...dateStyle }}
      />
      {/* Ours, because the native one cannot be made to read 24-hour.
          Same value shape, so the date half is unaffected. */}
      <Time24
        value={timePart.slice(0, 5)}
        disabled={disabled}
        required={required}
        onChange={t => emit(datePart, t)}
        style={{ ...base, width: 74, textAlign: 'center', ...timeStyle }}
      />
    </span>
  );
}
